import { useEffect, useRef, useState } from "react";
import { api } from "../../lib/api";
import { useSettings } from "../../context/SettingsContext";
import { useAuth } from "../../context/AuthContext";
import { toast } from "../ui/sonner";
import { Switch } from "../ui/switch";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../ui/dialog";
import { Logo } from "../Logo";

const FEATURES = [
  { key: "notify_match_enabled", label: "Match Email Notifications", desc: "Email a Stranger the moment they're matched." },
  { key: "notify_gallery_enabled", label: "Gallery Ready Email Notifications", desc: "Email a Stranger when their session gallery is ready." },
  { key: "messaging_enabled", label: "In-App Messaging", desc: "Let photographers message registered Strangers before session day." },
  { key: "stories_page_enabled", label: "Stranger Stories Page", desc: "Show the public Real People. Real Stories. page." },
];

const PLATFORM_TOGGLES = [
  { key: "photographer_approval_required", label: "Photographer Approval Required", desc: "Require admin review before a photographer's verification is approved. Turning this off auto-approves new photographers." },
  { key: "session_approval_required", label: "Session Approval Required", desc: "New sessions stay hidden from Strangers until an admin approves them." },
  { key: "waitlist_enabled", label: "Waitlist Enabled", desc: "Let Strangers join a waitlist once a session's spots are full instead of being blocked." },
  { key: "maintenance_mode", label: "Maintenance Mode", desc: "Show a maintenance page to all visitors except admins. Use this to safely take the site offline." },
];

const NUMERIC_KEYS = [
  "default_stranger_booking_fee",
  "default_photographer_booking_fee",
  "default_discount_amount",
  "default_discount_percentage",
  "default_session_capacity",
  "registration_cutoff_hours",
  "minimum_participant_age",
  "default_search_radius",
  "match_base_score",
  "match_session_preference_bonus",
  "match_interest_bonus_per_shared",
  "match_interest_bonus_max",
  "match_personality_bonus",
];

const MATCH_WEIGHT_FIELDS = [
  { key: "match_base_score", label: "Base Score (compatible pairing preferences)" },
  { key: "match_session_preference_bonus", label: "Bonus: Same Session-Type Preference" },
  { key: "match_interest_bonus_per_shared", label: "Bonus per Shared Interest" },
  { key: "match_interest_bonus_max", label: "Max Bonus from Shared Interests" },
  { key: "match_personality_bonus", label: "Bonus: Same Personality Type" },
];

const SOCIAL_FIELDS = [
  { key: "social_instagram_url", label: "Instagram URL" },
  { key: "social_facebook_url", label: "Facebook URL" },
  { key: "social_youtube_url", label: "YouTube URL" },
  { key: "social_tiktok_url", label: "TikTok URL" },
];

export const SettingsTab = () => {
  const { settings, refreshSettings } = useSettings();
  const { user } = useAuth();
  const [local, setLocal] = useState(settings);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileInput = useRef(null);
  const [purgeConfirmOpen, setPurgeConfirmOpen] = useState(false);
  const [purgeConfirmText, setPurgeConfirmText] = useState("");
  const [purging, setPurging] = useState(false);
  const [seeding, setSeeding] = useState(false);

  useEffect(() => {
    setLocal(settings);
  }, [settings]);

  const save = async () => {
    setSaving(true);
    const payload = { ...local };
    NUMERIC_KEYS.forEach((k) => {
      if (payload[k] !== undefined && payload[k] !== "") payload[k] = Number(payload[k]);
    });
    try {
      await api.put("/settings", payload);
      await refreshSettings();
      toast.success("Settings saved");
    } finally {
      setSaving(false);
    }
  };

  const uploadLogo = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    const formData = new FormData();
    formData.append("file", file);
    try {
      await api.post("/branding/logo", formData, { headers: { "Content-Type": "multipart/form-data" } });
      await refreshSettings();
      toast.success("Logo updated");
    } catch {
      toast.error("Couldn't upload logo. Try a PNG, JPG, WEBP, or SVG.");
    } finally {
      setUploading(false);
      if (fileInput.current) fileInput.current.value = "";
    }
  };

  const resetLogo = async () => {
    await api.delete("/branding/logo");
    await refreshSettings();
    toast.success("Reverted to default logo");
  };

  const purgeData = async () => {
    setPurging(true);
    try {
      const { data } = await api.post("/admin/demo-data/purge");
      toast.success(`Purged: ${Object.entries(data.deleted).filter(([, v]) => v > 0).map(([k, v]) => `${v} ${k.replace(/_/g, " ")}`).join(", ") || "nothing to purge"}`);
      setPurgeConfirmOpen(false);
      setPurgeConfirmText("");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't purge data");
    } finally {
      setPurging(false);
    }
  };

  const seedDemoData = async () => {
    setSeeding(true);
    try {
      await api.post("/admin/demo-data/seed");
      toast.success("Created 1 demo Photographer, 1 demo Stranger, and 1 demo Session.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't seed demo data");
    } finally {
      setSeeding(false);
    }
  };

  return (
    <div data-testid="settings-tab" className="space-y-14">
      <div>
        <h2 className="font-extrabold uppercase text-lg mb-4">Site Branding</h2>
        <div className="border border-soft-gray rounded-2xl p-6 flex items-center justify-between flex-wrap gap-6">
          <div className="bg-midnight-black rounded-xl px-4 py-3">
            <Logo className="h-9" />
          </div>
          <div className="flex items-center gap-3">
            <input ref={fileInput} type="file" accept="image/png,image/jpeg,image/webp,image/svg+xml" data-testid="logo-upload-input" onChange={uploadLogo} disabled={uploading} className="text-xs" />
            {settings.has_custom_logo && (
              <Button variant="outline" data-testid="reset-logo-button" onClick={resetLogo} className="rounded-full font-bold text-xs">
                RESET TO DEFAULT
              </Button>
            )}
          </div>
        </div>
      </div>

      <div>
        <h2 className="font-extrabold uppercase text-lg mb-4">Platform Settings</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-[10px] font-bold uppercase tracking-wide text-midnight-black/50 mb-1">Platform Name</label>
            <Input data-testid="setting-platform-name" value={local.platform_name || ""} onChange={(e) => setLocal({ ...local, platform_name: e.target.value })} />
          </div>
          <div>
            <label className="block text-[10px] font-bold uppercase tracking-wide text-midnight-black/50 mb-1">Support Email</label>
            <Input data-testid="setting-support-email" type="email" value={local.support_email || ""} onChange={(e) => setLocal({ ...local, support_email: e.target.value })} />
          </div>
          <div>
            <label className="block text-[10px] font-bold uppercase tracking-wide text-midnight-black/50 mb-1">Default Session Capacity</label>
            <Input data-testid="setting-default-capacity" type="number" value={local.default_session_capacity ?? ""} onChange={(e) => setLocal({ ...local, default_session_capacity: e.target.value })} />
          </div>
          <div>
            <label className="block text-[10px] font-bold uppercase tracking-wide text-midnight-black/50 mb-1">Registration Cutoff (hours before session)</label>
            <Input data-testid="setting-registration-cutoff" type="number" value={local.registration_cutoff_hours ?? ""} onChange={(e) => setLocal({ ...local, registration_cutoff_hours: e.target.value })} />
          </div>
          <div>
            <label className="block text-[10px] font-bold uppercase tracking-wide text-midnight-black/50 mb-1">Minimum Participant Age</label>
            <Input data-testid="setting-minimum-age" type="number" value={local.minimum_participant_age ?? ""} onChange={(e) => setLocal({ ...local, minimum_participant_age: e.target.value })} />
          </div>
          <div>
            <label className="block text-[10px] font-bold uppercase tracking-wide text-midnight-black/50 mb-1">Default Search Radius (miles)</label>
            <Input data-testid="setting-default-radius" type="number" value={local.default_search_radius ?? ""} onChange={(e) => setLocal({ ...local, default_search_radius: e.target.value })} />
          </div>
          <div className="sm:col-span-2">
            <label className="block text-[10px] font-bold uppercase tracking-wide text-midnight-black/50 mb-1">Default Cancellation Policy</label>
            <Textarea data-testid="setting-default-cancellation-policy" rows={3} value={local.default_cancellation_policy || ""} onChange={(e) => setLocal({ ...local, default_cancellation_policy: e.target.value })} placeholder="Pre-fills new session listings — photographers can still override it per session." />
          </div>
        </div>
        <div className="space-y-1 mt-6">
          {PLATFORM_TOGGLES.map((f) => (
            <div key={f.key} className="flex items-center justify-between gap-4 py-5 border-b border-soft-gray" data-testid={`setting-row-${f.key}`}>
              <div>
                <p className="font-bold text-sm">{f.label}</p>
                <p className="text-xs text-midnight-black/60 mt-1">{f.desc}</p>
              </div>
              <Switch checked={!!local[f.key]} onCheckedChange={(v) => setLocal({ ...local, [f.key]: v })} data-testid={`setting-switch-${f.key}`} />
            </div>
          ))}
        </div>
        <p className="text-xs text-midnight-black/40 mt-4">Session types shown to Strangers and photographers are managed under Content → Session Types. API keys and payment credentials always live in server environment variables, never in these settings.</p>
        <Button data-testid="save-platform-settings-button" onClick={save} disabled={saving} className="mt-8 bg-stranger-orange hover:bg-orange-600 text-clean-white font-bold rounded-full px-8">
          {saving ? "Saving..." : "SAVE SETTINGS"}
        </Button>
      </div>

      <div>
        <h2 className="font-extrabold uppercase text-lg mb-4">Matching Weights</h2>
        <p className="text-xs text-midnight-black/50 mb-4 max-w-xl">
          Controls how the matching engine scores compatible pairs (out of 100, capped). Gender / pairing-comfort
          compatibility is always a hard requirement and isn't adjustable here — these weights only affect ranking among compatible pairs.
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {MATCH_WEIGHT_FIELDS.map((f) => (
            <div key={f.key}>
              <label className="block text-[10px] font-bold uppercase tracking-wide text-midnight-black/50 mb-1">{f.label}</label>
              <Input data-testid={`setting-${f.key}`} type="number" value={local[f.key] ?? ""} onChange={(e) => setLocal({ ...local, [f.key]: e.target.value })} />
            </div>
          ))}
        </div>
        <Button data-testid="save-matching-settings-button" onClick={save} disabled={saving} className="mt-8 bg-stranger-orange hover:bg-orange-600 text-clean-white font-bold rounded-full px-8">
          {saving ? "Saving..." : "SAVE SETTINGS"}
        </Button>
      </div>

      <div>
        <h2 className="font-extrabold uppercase text-lg mb-4">Social Media Links</h2>
        <p className="text-xs text-midnight-black/50 mb-4 max-w-xl">
          Leave a field blank to hide that icon from the site footer entirely. Only icons with a link filled in are shown.
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {SOCIAL_FIELDS.map((f) => (
            <div key={f.key}>
              <label className="block text-[10px] font-bold uppercase tracking-wide text-midnight-black/50 mb-1">{f.label}</label>
              <Input data-testid={`setting-${f.key}`} placeholder="https://..." value={local[f.key] ?? ""} onChange={(e) => setLocal({ ...local, [f.key]: e.target.value })} />
            </div>
          ))}
        </div>
        <Button data-testid="save-social-settings-button" onClick={save} disabled={saving} className="mt-8 bg-stranger-orange hover:bg-orange-600 text-clean-white font-bold rounded-full px-8">
          {saving ? "Saving..." : "SAVE SETTINGS"}
        </Button>
      </div>

      <div>
        <h2 className="font-extrabold uppercase text-lg mb-4">Feature Toggles</h2>
        <div className="space-y-1">
          {FEATURES.map((f) => (
            <div key={f.key} className="flex items-center justify-between gap-4 py-5 border-b border-soft-gray" data-testid={`setting-row-${f.key}`}>
              <div>
                <p className="font-bold text-sm">{f.label}</p>
                <p className="text-xs text-midnight-black/60 mt-1">{f.desc}</p>
              </div>
              <Switch checked={!!local[f.key]} onCheckedChange={(v) => setLocal({ ...local, [f.key]: v })} data-testid={`setting-switch-${f.key}`} />
            </div>
          ))}
        </div>
        <Button data-testid="save-settings-button" onClick={save} disabled={saving} className="mt-8 bg-stranger-orange hover:bg-orange-600 text-clean-white font-bold rounded-full px-8">
          {saving ? "Saving..." : "SAVE SETTINGS"}
        </Button>
      </div>

      <div>
        <h2 className="font-extrabold uppercase text-lg mb-4">Booking Fees</h2>
        <div className="space-y-1">
          <div className="flex items-center justify-between gap-4 py-5 border-b border-soft-gray" data-testid="setting-row-booking_fees_enabled">
            <div>
              <p className="font-bold text-sm">Booking Fees Enabled</p>
              <p className="text-xs text-midnight-black/60 mt-1">Charge a platform booking fee on new bookings. Turning this off makes every new booking free.</p>
            </div>
            <Switch checked={!!local.booking_fees_enabled} onCheckedChange={(v) => setLocal({ ...local, booking_fees_enabled: v })} data-testid="setting-switch-booking_fees_enabled" />
          </div>
          <div className="flex items-center justify-between gap-4 py-5 border-b border-soft-gray" data-testid="setting-row-discounted_booking_fees_enabled">
            <div>
              <p className="font-bold text-sm">Promotions / Discounted Fees</p>
              <p className="text-xs text-midnight-black/60 mt-1">Allow active promotions to automatically discount booking fees.</p>
            </div>
            <Switch checked={!!local.discounted_booking_fees_enabled} onCheckedChange={(v) => setLocal({ ...local, discounted_booking_fees_enabled: v })} data-testid="setting-switch-discounted_booking_fees_enabled" />
          </div>
          <div className="flex items-center justify-between gap-4 py-5 border-b border-soft-gray" data-testid="setting-row-free_booking_option_enabled">
            <div>
              <p className="font-bold text-sm">Free / Complimentary Bookings</p>
              <p className="text-xs text-midnight-black/60 mt-1">Allow sessions to be marked complimentary (no booking fee for either party).</p>
            </div>
            <Switch checked={!!local.free_booking_option_enabled} onCheckedChange={(v) => setLocal({ ...local, free_booking_option_enabled: v })} data-testid="setting-switch-free_booking_option_enabled" />
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-6">
          <div>
            <label className="block text-[10px] font-bold uppercase tracking-wide text-midnight-black/50 mb-1">Default Stranger Booking Fee ($)</label>
            <Input data-testid="setting-default-stranger-fee" type="number" value={local.default_stranger_booking_fee ?? ""} onChange={(e) => setLocal({ ...local, default_stranger_booking_fee: e.target.value })} />
          </div>
          <div>
            <label className="block text-[10px] font-bold uppercase tracking-wide text-midnight-black/50 mb-1">Default Photographer Booking Fee ($)</label>
            <Input data-testid="setting-default-photographer-fee" type="number" value={local.default_photographer_booking_fee ?? ""} onChange={(e) => setLocal({ ...local, default_photographer_booking_fee: e.target.value })} />
          </div>
          <div>
            <label className="block text-[10px] font-bold uppercase tracking-wide text-midnight-black/50 mb-1">Default Discount Amount ($)</label>
            <Input data-testid="setting-default-discount-amount" type="number" value={local.default_discount_amount ?? ""} onChange={(e) => setLocal({ ...local, default_discount_amount: e.target.value })} />
          </div>
          <div>
            <label className="block text-[10px] font-bold uppercase tracking-wide text-midnight-black/50 mb-1">Default Discount Percentage (%)</label>
            <Input data-testid="setting-default-discount-percentage" type="number" value={local.default_discount_percentage ?? ""} onChange={(e) => setLocal({ ...local, default_discount_percentage: e.target.value })} />
          </div>
          <div>
            <label className="block text-[10px] font-bold uppercase tracking-wide text-midnight-black/50 mb-1">Platform Venmo Username</label>
            <Input data-testid="setting-venmo-username" value={local.venmo_username || ""} onChange={(e) => setLocal({ ...local, venmo_username: e.target.value })} />
          </div>
          <div>
            <label className="block text-[10px] font-bold uppercase tracking-wide text-midnight-black/50 mb-1">Platform Cash App Username</label>
            <Input data-testid="setting-cashapp-username" value={local.cashapp_username || ""} onChange={(e) => setLocal({ ...local, cashapp_username: e.target.value })} />
          </div>
        </div>
        <Button data-testid="save-fee-settings-button" onClick={save} disabled={saving} className="mt-8 bg-stranger-orange hover:bg-orange-600 text-clean-white font-bold rounded-full px-8">
          {saving ? "Saving..." : "SAVE SETTINGS"}
        </Button>
      </div>

      {user?.role === "super_admin" && (
        <div>
          <h2 className="font-extrabold uppercase text-lg mb-4 text-red-600">Danger Zone</h2>
          <div className="border-2 border-red-200 bg-red-50 rounded-2xl p-6 space-y-6">
            <div className="flex items-center justify-between gap-4 flex-wrap">
              <div>
                <p className="font-bold text-sm">Purge All Photographer &amp; Stranger Data</p>
                <p className="text-xs text-midnight-black/60 mt-1 max-w-md">
                  Permanently deletes every Photographer, Stranger, session, booking, match, review, safety report, and fee. Your Admin account and Platform Settings are untouched. This cannot be undone.
                </p>
              </div>
              <Button
                data-testid="purge-data-button"
                variant="outline"
                onClick={() => setPurgeConfirmOpen(true)}
                className="rounded-full font-bold text-xs border-2 border-red-600 text-red-600 hover:bg-red-600 hover:text-clean-white shrink-0"
              >
                PURGE ALL DATA
              </Button>
            </div>
            <div className="flex items-center justify-between gap-4 flex-wrap border-t border-red-200 pt-6">
              <div>
                <p className="font-bold text-sm">Seed One Demo Instance</p>
                <p className="text-xs text-midnight-black/60 mt-1 max-w-md">
                  Creates exactly 1 demo Photographer, 1 demo Stranger (registered for the session), and 1 demo Session — so visitors see a real-looking listing.
                </p>
              </div>
              <Button
                data-testid="seed-demo-data-button"
                onClick={seedDemoData}
                disabled={seeding}
                className="rounded-full font-bold text-xs bg-midnight-black hover:bg-black text-clean-white shrink-0"
              >
                {seeding ? "CREATING..." : "SEED DEMO DATA"}
              </Button>
            </div>
          </div>

          <Dialog open={purgeConfirmOpen} onOpenChange={(v) => { setPurgeConfirmOpen(v); if (!v) setPurgeConfirmText(""); }}>
            <DialogContent data-testid="purge-confirm-dialog">
              <DialogHeader>
                <DialogTitle className="font-extrabold uppercase text-red-600">Purge all data?</DialogTitle>
                <DialogDescription>
                  This permanently deletes every Photographer, Stranger, session, booking, match, review, and fee on the site. Your Admin account and Platform Settings will NOT be affected. This cannot be undone.
                </DialogDescription>
              </DialogHeader>
              <p className="text-xs font-bold uppercase tracking-wide text-midnight-black/60 mb-1">Type DELETE to confirm</p>
              <Input data-testid="purge-confirm-input" value={purgeConfirmText} onChange={(e) => setPurgeConfirmText(e.target.value)} placeholder="DELETE" />
              <DialogFooter>
                <Button variant="outline" data-testid="purge-confirm-cancel" onClick={() => { setPurgeConfirmOpen(false); setPurgeConfirmText(""); }} className="rounded-full font-bold">
                  Cancel
                </Button>
                <Button
                  data-testid="purge-confirm-submit"
                  disabled={purgeConfirmText !== "DELETE" || purging}
                  onClick={purgeData}
                  className="rounded-full font-bold text-clean-white bg-red-600 hover:bg-red-700"
                >
                  {purging ? "PURGING..." : "PERMANENTLY DELETE"}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      )}
    </div>
  );
};
